import type { Stripe, StripeElements } from "@stripe/stripe-js";

import { confirmSetupIntent } from "./confirmSetupIntent";

/**
 * After Stripe SetupIntent success, PaymentProfile is created by the
 * setup_intent.succeeded webhook. DAT-1731 / FR-1292.
 */
export const PAYMENT_PROFILE_LIST_POLL_TIMEOUT_MS = 90_000;
export const PAYMENT_PROFILE_LIST_POLL_INTERVAL_MS = 1_500;

export async function waitForPaymentProfileCountIncrease({
  fetchCount,
  baseline,
  isAborted = () => false,
  timeoutMs = PAYMENT_PROFILE_LIST_POLL_TIMEOUT_MS,
  intervalMs = PAYMENT_PROFILE_LIST_POLL_INTERVAL_MS,
}: {
  fetchCount: () => Promise<number | undefined>;
  baseline: number;
  isAborted?: () => boolean;
  timeoutMs?: number;
  intervalMs?: number;
}): Promise<boolean> {
  const startTime = Date.now();

  while (!isAborted() && Date.now() - startTime < timeoutMs) {
    try {
      const count = await fetchCount();
      if (isAborted()) return false;
      if (typeof count === "number" && count > baseline) return true;
    } catch {
      // Keep polling through transient list errors.
    }
    if (Date.now() - startTime >= timeoutMs) return false;
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  return false;
}

export type ConfirmSetupProfileOutcome =
  | { status: "created" }
  | { status: "confirm_error"; message: string }
  | { status: "not_created" };

/**
 * Confirm the SetupIntent and wait until the payment-profile list grows.
 * List polling starts immediately so a hung `confirmSetup` cannot hide a
 * webhook that already wrote the row (FR-1292). A list miss is only treated
 * as not-created after Stripe has succeeded, so a long 3DS challenge cannot
 * remint the SetupIntent or eat the webhook wait.
 */
export async function confirmSetupAndWaitForPaymentProfile({
  stripe,
  elements,
  confirmParams,
  clientSecret,
  fetchCount,
  baseline,
  isAborted = () => false,
  timeoutMs,
  intervalMs,
}: {
  stripe: Stripe;
  elements: StripeElements;
  confirmParams: {
    return_url: string;
    payment_method_data?: {
      billing_details?: { address?: { line2?: string | null } };
    };
  };
  clientSecret?: string | null;
  fetchCount?: () => Promise<number | undefined>;
  baseline: number;
  isAborted?: () => boolean;
  timeoutMs?: number;
  intervalMs?: number;
}): Promise<ConfirmSetupProfileOutcome> {
  const confirm = confirmSetupIntent(
    stripe,
    elements,
    confirmParams,
    clientSecret
  );

  if (!fetchCount) {
    const result = await confirm;
    if (result.error) {
      return {
        status: "confirm_error",
        message: result.error.message || "",
      };
    }
    return { status: "not_created" };
  }

  let stopEarlyPoll = false;
  const listPoll = waitForPaymentProfileCountIncrease({
    fetchCount,
    baseline,
    isAborted: () => stopEarlyPoll || isAborted(),
    timeoutMs: Number.POSITIVE_INFINITY,
    intervalMs,
  });

  const first = await Promise.race([
    listPoll.then((appeared) => ({ source: "list" as const, appeared })),
    confirm.then((result) => ({ source: "confirm" as const, result })),
  ]);

  if (first.source === "list") {
    stopEarlyPoll = true;
    return first.appeared ? { status: "created" } : { status: "not_created" };
  }

  if (first.result.error) {
    stopEarlyPoll = true;
    return {
      status: "confirm_error",
      message: first.result.error.message || "",
    };
  }

  stopEarlyPoll = true;
  // Fresh webhook window from Stripe success, not leftover time from 3DS.
  const appeared = await waitForPaymentProfileCountIncrease({
    fetchCount,
    baseline,
    isAborted,
    timeoutMs,
    intervalMs,
  });
  return appeared ? { status: "created" } : { status: "not_created" };
}
